import { DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, of, Subject, switchMap, take, takeUntil, tap } from 'rxjs';

import { FILE_BROWSER_ACCEPTED_EXTENSIONS, FILE_BROWSER_ENV_CONFIG, FILE_BROWSER_OPEN_DRIVE_FILE_ACTION, FILE_BROWSER_OPEN_LOCAL_FILE_ACTION } from '../../file-browser.token';
import { DataEnum, FileItem } from '../../models';
import { DriveFileBrowserService, LocalFileBrowserService } from '../../services';

export type Navigation = 'drive' | 'local';

@Component({
  selector: 'evi-desktop-file-browser-modal',
  templateUrl: './file-browser-modal.component.html',
  styleUrls: ['./file-browser-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FileBrowserModalComponent implements OnInit, OnDestroy {
  public static readonly WIDTH = '900px';
  public static readonly HEIGHT = '700px';

  public readonly acceptedExtensions: string[] = inject<() => string[]>(FILE_BROWSER_ACCEPTED_EXTENSIONS)();

  private readonly _dialogRef: DialogRef = inject(DialogRef);
  private readonly _driveFileBrowserService: DriveFileBrowserService = inject(DriveFileBrowserService);
  private readonly _localFileBrowserService: LocalFileBrowserService = inject(LocalFileBrowserService);
  private readonly _openLocalFileAction: (file: FileItem) => void = inject(FILE_BROWSER_OPEN_LOCAL_FILE_ACTION);
  private readonly _openDriveFileAction: (file: FileItem) => void = inject(FILE_BROWSER_OPEN_DRIVE_FILE_ACTION);
  private readonly _envConfig: { isLocal: boolean, isCloudConnected: boolean } = inject(FILE_BROWSER_ENV_CONFIG);

  private readonly _unsubscriber: Subject<void> = new Subject<void>();
  private readonly _activeNavigationSubject: BehaviorSubject<Navigation | null> = new BehaviorSubject<Navigation | null>(this.defaultNavigation);
  private readonly _navigationLoadFn: { [key in Navigation]: () => void } = {
    drive: this.openDrive.bind(this),
    local: this.openLocal.bind(this),
  };

  private readonly _activeFileBrowserService$: Observable<DriveFileBrowserService | LocalFileBrowserService> = this._activeNavigationSubject.pipe(
    map((navigation: Navigation | null) => navigation === 'local'
      ? this._localFileBrowserService
      : this._driveFileBrowserService,
    ),
    takeUntil(this._unsubscriber),
  );

  public loading$: Observable<boolean> = this._activeFileBrowserService$.pipe(
    switchMap(service => service.isLoading$),
    takeUntil(this._unsubscriber),
  );

  public list$: Observable<FileItem[]> = this._activeFileBrowserService$.pipe(
    switchMap(service => service.getItems$),
    takeUntil(this._unsubscriber),
  );

  public path$: Observable<FileItem[]> = this._activeFileBrowserService$.pipe(
    switchMap(service => service.getBreadcrumb$),
    tap(path => this._path = path),
    takeUntil(this._unsubscriber),
  );

  private _hasDriveUnauthorizedError = false;
  private _path: FileItem[] = [];

  public get authorizations(): { [key in Navigation]: { active: boolean, hasUnauthorizedError?: boolean } } {
    return {
      drive: { active: this._envConfig.isCloudConnected, hasUnauthorizedError: this._hasDriveUnauthorizedError },
      local: { active: this._envConfig.isLocal },
    };
  }

  public get activeNavigation(): Navigation | null {
    return this._activeNavigationSubject.value;
  }

  public get disableBackButton(): boolean {
    return this._path.length <= 1;
  }

  private get defaultNavigation(): Navigation {
    if (this._envConfig.isLocal) {
      return 'local';
    }

    return 'drive';
  }

  public ngOnInit(): void {
    this.loadDataFromNavigation();
  }

  public ngOnDestroy(): void {
    this._activeNavigationSubject.complete();
    this._unsubscriber.complete();
  }

  public isFile(item: FileItem): boolean {
    return item.type === DataEnum.FILE;
  }

  public isFolder(item: FileItem): boolean {
    return item.type === DataEnum.FOLDER;
  }

  public openDrive(): void {
    this._localFileBrowserService.resetState();
    this._driveFileBrowserService.resetState();
    this._driveFileBrowserService.setRoot({ name: 'Drive' });
    this.loadItems();

    this._activeNavigationSubject.next('drive');
  }

  public openLocal(): void {
    this._driveFileBrowserService.resetState();
    this._localFileBrowserService.resetState();

    this._activeNavigationSubject.next('local');

    this._localFileBrowserService.getStorages().pipe(
      take(1),
      takeUntil(this._unsubscriber),
    ).subscribe((storages: FileItem[]) => {
      const [firstStorage] = storages;

      if (!firstStorage) {
        return;
      }

      this._localFileBrowserService.setRoot({ name: firstStorage.name, id: firstStorage.id });
      this.loadLocalItems();
    });
  }

  public backToPreviousFolder(breadcrumbLength: number): void {
    if (!breadcrumbLength) {
      return;
    }

    if (breadcrumbLength === 1) {
      this._activeNavigationSubject.next(null);
    }

    if (this.activeNavigation === 'local') {
      const localFolder: FileItem | null | undefined = this._localFileBrowserService.goBack();

      if (localFolder) {
        this.loadLocalItems(localFolder);
      }

      return;
    }

    const folder: FileItem | null | undefined = this._driveFileBrowserService.goBack();

    if (folder) {
      this.loadItems(folder);
    }
  }

  public onSelectFolder(folder: FileItem): void {
    if (this.activeNavigation === 'local') {
      this._localFileBrowserService.navigateToItem({ folder });
      this.loadLocalItems(folder);

      return;
    }

    this._driveFileBrowserService.navigateToItem({ folder });
    this.loadItems(folder);
  }

  public onSelectFile(file: FileItem): void {
    this._dialogRef.close();

    if (this.activeNavigation === 'local') {
      this._openLocalFileAction(file);

      return;
    }

    this._openDriveFileAction(file);
  }

  public onClose(): void {
    this._dialogRef.close();
  }

  private loadDataFromNavigation(): void {
    this._navigationLoadFn[this.defaultNavigation]();
  }

  private loadItems(parent: FileItem | null = null): void {
    this._driveFileBrowserService.loadItems(parent).pipe(
      take(1),
      tap(() => this._hasDriveUnauthorizedError = false),
      catchError((error): Observable<any> => {
        this._hasDriveUnauthorizedError = error.includes('401');

        return of(error);
      }),
    ).subscribe();
  }

  private loadLocalItems(parent: FileItem | null = null): void {
    this._localFileBrowserService.loadItems(parent).pipe(
      take(1),
      takeUntil(this._unsubscriber),
    ).subscribe();
  }
}
